const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const normalizeBoolish = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  if (typeof v === "number" && Number.isFinite(v)) return v !== 0;
  const s = toStr(v).trim().toLowerCase();
  if (["1", "true", "yes", "ok", "success"].includes(s)) return true;
  if (["0", "false", "no"].includes(s)) return false;
  return null;
};

const hasAnyKeys = (obj) =>
  obj &&
  typeof obj === "object" &&
  !Array.isArray(obj) &&
  Object.keys(obj).length > 0;

const normalizeStatusText = (raw) => toStr(raw).trim().toLowerCase();

const isDemoOrder = (order) => Boolean(order?.demo);

const USER_CANCEL_REASONS = ["CANCELLED_BY_USER", "CANCELED_BY_USER"];

export const isPaymentCancelledByUser = (failedTransactionData) => {
  const reason = toStr(
    failedTransactionData?.status_reason ?? failedTransactionData?.statusReason,
  )
    .trim()
    .toUpperCase();
  if (USER_CANCEL_REASONS.includes(reason)) return true;

  const status = toStr(failedTransactionData?.status).trim().toUpperCase();
  const looksLikePaymentTransaction =
    failedTransactionData?.payment_request_id != null ||
    failedTransactionData?.transaction_type != null ||
    failedTransactionData?.terminal_id != null ||
    failedTransactionData?.requested_amount != null;
  return status === "CANCELLED" && looksLikePaymentTransaction;
};

const containsPaymentCancelledByUser = (value, depth = 0) => {
  if (!value || typeof value !== "object" || depth > 4) return false;
  if (isPaymentCancelledByUser(value)) return true;
  return Object.values(value).some((child) =>
    containsPaymentCancelledByUser(child, depth + 1),
  );
};

export const isOrderPaymentCancelledByUser = (order) => {
  const directCandidates = [
    order?.failedTransactionData,
    order?.paymentErrorResponse,
    order?.posError,
    order?.posError?.failedTransactionData,
    order?.posError?.paymentErrorResponse,
    order?.terminalError,
  ];
  if (directCandidates.some(isPaymentCancelledByUser)) return true;
  return containsPaymentCancelledByUser(order);
};

const isCancelledByCustomer = (order) => {
  const status = normalizeStatusText(order?.orderStatus);
  const reason = normalizeStatusText(order?.posError?.reason);
  if (reason === "emv_user_cancel") return true;
  return (
    status.includes("cancel") ||
    status.includes("canceled") ||
    status.includes("cancelled") ||
    status.includes("user cancel")
  );
};

const isPaymentFailed = (order) => {
  const status = normalizeStatusText(order?.orderStatus);
  if (status.includes("payment failed")) return true;
  return hasAnyKeys(order?.posError);
};

const looksPending = (order) => {
  const status = normalizeStatusText(order?.orderStatus);
  return (
    status.includes("pending") ||
    status.includes("in progress") ||
    status.includes("processing")
  );
};

/**
 * Order status mapping for UI icons/labels.
 * NOTE: This is intentionally conservative: we never infer success from "payment method exists"
 * or "order was sent".
 *
 * Manual verification checklist (UI):
 * - Cash/card method but no payment execution evidence -> payment = not_executed (grey)
 * - Card method but terminal not executed -> terminal = not_executed (grey)
 * - Terminal approved (explicit response/tx) -> terminal = ok (green)
 * - Terminal declined/error -> terminal = error (red)
 * - E‑KASA missing receipt -> ekasa = not_executed (grey)
 * - E‑KASA confirmed (explicit status/receipt id) -> ekasa = ok (green)
 * - Demo order -> demo (orange), no misleading ok
 * - Cancelled-by-customer -> cancelled indicator; no contradictory ok unless explicitly recorded
 */
export const mapOrderStatus = (order) => {
  if (isDemoOrder(order)) {
    return { labelKey: null, state: "demo", debugReason: "demo=true" };
  }
  if (isCancelledByCustomer(order)) {
    return {
      labelKey: null,
      state: "cancelled",
      debugReason: "orderStatus/posError indicates customer cancellation",
    };
  }
  if (isOrderPaymentCancelledByUser(order)) {
    return {
      labelKey: null,
      state: "cancelled",
      debugReason: "failedTransactionData.status_reason=user cancelled",
    };
  }
  if (isPaymentFailed(order)) {
    return {
      labelKey: null,
      state: "error",
      debugReason: "posError present or orderStatus=payment failed",
    };
  }
  if (looksPending(order)) {
    return {
      labelKey: null,
      state: "pending",
      debugReason: "orderStatus indicates pending/processing",
    };
  }
  // If backend has a dedicated order status flag, use it (optional).
  const okFlag = normalizeBoolish(order?.orderStatusOk ?? order?.statusOk);
  if (okFlag === true) {
    return {
      labelKey: null,
      state: "ok",
      debugReason: "orderStatusOk/statusOk=true",
    };
  }
  // Explicit "complete" orderStatus → ok (green)
  if (normalizeStatusText(order?.orderStatus) === "complete") {
    return {
      labelKey: null,
      state: "ok",
      debugReason: "orderStatus=complete",
    };
  }
  // Free payment + created → ok (green) — order is valid, no payment needed
  const method = resolvePaymentMethod(order);
  if (
    normalizeStatusText(method) === "free" &&
    normalizeStatusText(order?.orderStatus) === "created"
  ) {
    return {
      labelKey: null,
      state: "ok",
      debugReason: "paymentMethod=free + orderStatus=created",
    };
  }
  // Web payment + paid → ok (green)
  if (
    normalizeStatusText(method) === "web" &&
    normalizeStatusText(order?.orderStatus) === "paid"
  ) {
    return {
      labelKey: null,
      state: "ok",
      debugReason: "paymentMethod=web + orderStatus=paid",
    };
  }
  // AlreadyPaid (POS) + created → ok (green)
  if (
    normalizeStatusText(method) === "alreadypaid" &&
    normalizeStatusText(order?.orderStatus) === "created"
  ) {
    return {
      labelKey: null,
      state: "ok",
      debugReason: "paymentMethod=alreadyPaid + orderStatus=created",
    };
  }
  // Cash + created → ok (green)
  if (
    normalizeStatusText(method) === "cash" &&
    normalizeStatusText(order?.orderStatus) === "created"
  ) {
    return {
      labelKey: null,
      state: "ok",
      debugReason: "paymentMethod=cash + orderStatus=created",
    };
  }
  return {
    labelKey: null,
    state: "unknown",
    debugReason: order?.orderStatus
      ? `orderStatus=${toStr(order.orderStatus)}`
      : "missing orderStatus",
  };
};

const resolvePaymentMethod = (order) =>
  order?.payment?.method ??
  order?.paymentMethod ??
  order?.payment_method ??
  order?.paymentType ??
  order?.payment_type ??
  "";

const paymentSuccessEvidence = (order) => {
  const p = order?.payment;
  // Cash machine (ALBACON/Glory): an approved cash payment IS executed. The cash-machine
  // transaction state lives on order.cashPayment, not order.payment.
  if (normalizeStatusText(order?.cashPayment?.status) === "approved") {
    return { ok: true, why: "cashPayment.status=approved" };
  }
  const status = normalizeStatusText(p?.status);
  const paid = normalizeBoolish(p?.paid ?? p?.isPaid ?? p?.success);
  const txId = p?.transactionId ?? p?.transactionID ?? p?.txId ?? p?.id ?? null;
  if (paid === true) return { ok: true, why: "payment.paid/success=true" };
  if (status === "success" || status === "paid")
    return { ok: true, why: `payment.status=${status}` };
  if (txId) return { ok: true, why: "payment.transactionId/id present" };
  return { ok: false, why: "no explicit payment success fields" };
};

const terminalSuccessEvidence = (order) => {
  const posOk = normalizeBoolish(
    order?.posStatus ?? order?.terminalStatus ?? order?.pos_status,
  );
  const hasPosResp = hasAnyKeys(order?.posResponse ?? order?.terminalResponse);
  const hasTx =
    Boolean(
      order?.posResponse?.transactionID ?? order?.posResponse?.transactionId,
    ) ||
    Boolean(
      order?.terminalResponse?.transactionID ??
      order?.terminalResponse?.transactionId,
    );
  if (posOk === true && (hasPosResp || hasTx))
    return { ok: true, why: "posStatus=true and posResponse present" };
  if (posOk === true && !hasPosResp)
    return { ok: false, why: "posStatus=true but missing posResponse" };
  return { ok: false, why: "posStatus not true" };
};

export const isPaymentReversed = (order) =>
  order?.payment?.reversalState === "reversed";

export const mapPaymentStatus = (order) => {
  const method = resolvePaymentMethod(order);

  if (isDemoOrder(order)) {
    return { state: "demo", method, debugReason: "demo=true" };
  }
  // Stornovaná platba má prednosť — ikona musí ukázať storno bez ohľadu na pôvodný stav.
  if (isPaymentReversed(order)) {
    return { state: "reversed", method, debugReason: "payment.reversalState=reversed" };
  }
  if (isCancelledByCustomer(order)) {
    return { state: "cancelled", method, debugReason: "customer cancelled" };
  }
  if (isOrderPaymentCancelledByUser(order)) {
    return {
      state: "cancelled",
      method,
      debugReason: "failedTransactionData.status_reason=user cancelled",
    };
  }

  if (isPaymentFailed(order)) {
    return {
      state: "error",
      method,
      debugReason: "posError present or orderStatus=payment failed",
    };
  }

  if (looksPending(order)) {
    return {
      state: "pending",
      method,
      debugReason: "orderStatus indicates pending/processing",
    };
  }

  // Success: require explicit evidence.
  const payEv = paymentSuccessEvidence(order);
  if (payEv.ok) {
    return { state: "ok", method, debugReason: payEv.why };
  }

  // Terminal evidence can also confirm payment execution (card).
  const termEv = terminalSuccessEvidence(order);
  if (termEv.ok) {
    return { state: "ok", method, debugReason: termEv.why };
  }

  // If method exists but no success evidence, we consider it not executed (do not show OK).
  if (toStr(method).trim()) {
    return {
      state: "not_executed",
      method,
      debugReason: "payment method set but missing success evidence",
    };
  }
  return {
    state: "not_executed",
    method: "",
    debugReason: "missing payment method + no success evidence",
  };
};

export const mapTerminalStatus = (order) => {
  const provider = order?.posProvider ?? order?.terminalProvider ?? "";

  if (isDemoOrder(order)) {
    return { state: "demo", provider, debugReason: "demo=true" };
  }
  if (isCancelledByCustomer(order)) {
    return { state: "cancelled", provider, debugReason: "customer cancelled" };
  }
  if (isOrderPaymentCancelledByUser(order)) {
    return {
      state: "cancelled",
      provider,
      debugReason: "failedTransactionData.status_reason=user cancelled",
    };
  }
  if (hasAnyKeys(order?.posError ?? order?.terminalError)) {
    const reason =
      order?.posError?.reason ?? order?.terminalError?.reason ?? "";
    return {
      state: "error",
      provider,
      debugReason: reason
        ? `posError.reason=${toStr(reason)}`
        : "posError present",
    };
  }
  if (looksPending(order)) {
    return {
      state: "pending",
      provider,
      debugReason: "orderStatus indicates pending/processing",
    };
  }
  const ev = terminalSuccessEvidence(order);
  if (ev.ok) return { state: "ok", provider, debugReason: ev.why };

  // If payment method is card-like but no evidence, still not executed.
  const method = resolvePaymentMethod(order);
  if (toStr(method).trim()) {
    return { state: "not_executed", provider, debugReason: ev.why };
  }
  return {
    state: "not_executed",
    provider,
    debugReason: "no terminal interaction fields",
  };
};

const EKASA_ERROR_TEXT_MAX = 300;

const tryParseJsonObject = (value) => {
  const s = toStr(value).trim();
  if (!s.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(s);
    return hasAnyKeys(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
};

/**
 * eKasaError v dsokOrders má viac tvarov: objekt (Portos chyba), JSON string
 * (kiosk flow ho ukladá stringifikovaný, napr. DLA {"response":{"data":{code,message}}}),
 * alebo obyčajný text / HTML (napr. 502 z nginx). Vráti objekt, alebo null ak chyba nie je.
 */
export const parseEkasaError = (order) => {
  const raw = order?.eKasaError ?? order?.ekasaError ?? null;
  if (hasAnyKeys(raw)) return raw;
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const parsed = tryParseJsonObject(raw);
  if (parsed) return parsed;
  // HTML chybová stránka → jej <title>, inak text bez tagov.
  const title = raw.match(/<title>([^<]*)<\/title>/i)?.[1];
  const text = (title || raw.replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();
  return text ? { message: text } : null;
};

const flattenEkasaErrors = (errors) => {
  const obj =
    typeof errors === "string" ? tryParseJsonObject(errors) : errors;
  if (!hasAnyKeys(obj)) return "";
  return Object.values(obj)
    .flat()
    .map((v) => toStr(v).trim())
    .filter(Boolean)
    .join("; ");
};

/**
 * Čitateľný text chyby eKasa pre tooltip v zozname objednávok, napr.
 * "DLA_TIMEOUT: DLA response timeout" alebo "Certifikát exspiroval (-302): Certifikát ...".
 */
export const getEkasaErrorText = (order) => {
  const err = parseEkasaError(order);
  if (!err || err?.status === "no-error") return "";
  const wrapped = hasAnyKeys(err?.response?.data) ? err.response.data : err;
  // DLA chyba nesie aj pôvodnú odpoveď Portosu (title/detail) — tá je pre človeka užitočnejšia
  // ako všeobecné "Portos API returned HTTP 500".
  const portos = wrapped?.portosResponse;
  const d =
    hasAnyKeys(portos) && (portos.title || portos.detail || portos.errors)
      ? portos
      : wrapped;
  const code = d?.code != null ? toStr(d.code) : "";
  const title = toStr(d?.title).trim();
  const detail = [toStr(d?.detail).trim(), flattenEkasaErrors(d?.errors)]
    .filter(Boolean)
    .join("; ");
  const message = toStr(d?.message).trim();

  let text;
  if (title) {
    text = `${title}${code ? ` (${code})` : ""}${detail ? `: ${detail}` : ""}`;
  } else if (message || detail) {
    text = `${code ? `${code}: ` : ""}${message || detail}`;
  } else {
    text = code;
  }
  if (!text) text = JSON.stringify(d);
  return text.length > EKASA_ERROR_TEXT_MAX
    ? `${text.slice(0, EKASA_ERROR_TEXT_MAX)}…`
    : text;
};

export const mapEkasaStatus = (order) => {
  if (isDemoOrder(order)) {
    return { state: "demo", debugReason: "demo=true" };
  }
  if (isCancelledByCustomer(order)) {
    return { state: "cancelled", debugReason: "customer cancelled" };
  }

  const ekasaOk = normalizeBoolish(
    order?.ekasaStatus ?? order?.eKasaStatus ?? order?.ekasa_status,
  );
  const resp = order?.eKasaResponse ?? order?.ekasaResponse ?? null;
  const err = parseEkasaError(order);

  if (err) {
    if (err?.status === "no-error") {
      return { state: "ok", debugReason: "eKasaError.status is no-error" };
    }
    return { state: "error", debugReason: "eKasaError present" };
  }

  // Success: explicit ekasaStatus or explicit receipt evidence.
  const receiptId =
    resp?.receiptId ?? resp?.receipt_id ?? resp?.id ?? resp?.uid ?? null;
  if (ekasaOk === true) {
    return { state: "ok", debugReason: "ekasaStatus=true" };
  }
  if (receiptId) {
    return { state: "ok", debugReason: "eKasaResponse has id/uid/receiptId" };
  }

  if (looksPending(order)) {
    return {
      state: "pending",
      debugReason: "orderStatus indicates pending/processing",
    };
  }

  // If nothing indicates it ran, treat as not executed.
  if (ekasaOk === false) {
    return { state: "not_executed", debugReason: "ekasaStatus=false" };
  }
  if (hasAnyKeys(resp)) {
    return {
      state: "unknown",
      debugReason: "eKasaResponse present but missing expected receipt fields",
    };
  }
  return { state: "not_executed", debugReason: "missing eKasa fields" };
};

export const __private = {
  normalizeBoolish,
  hasAnyKeys,
  resolvePaymentMethod,
  normalizeStatusText,
  isPaymentCancelledByUser,
  isOrderPaymentCancelledByUser,
};
